import React, { useContext, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import styled, { ThemeContext } from 'styled-components';
import posthog from 'posthog-js';

import { H2Styles, H6Styles, S2Styles } from '../../../Typography';
import { isUserLoggedIn } from '../../../../utils/analytics';
import { useStore } from '../../../../store';
import config from '../../../../../config.json';
import CreatorScroller from '../../../CreatorScroller';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import Container from '../../../Container';

const { routes } = config;

const ProductCarouselWrapper = styled.div`
  flex: 1 1;
  position: relative;
  contain: layout;
  padding-right: 0px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    padding-right: 0px;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeDesktop}px) {
    padding-right: 20px;
  }
`;

const ProductCarouselHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
`;

const FeaturedCreatorsHeading = styled.h2`
  ${H6Styles}
  color: ${({ theme }) => theme.colors.primaryDark};
  text-transform: uppercase;
  margin-left: 16px;
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-weight: 900;
  font-size: 20px;
  line-height: 38px;
  color: ${({ theme }) => theme.colors.primaryDark};

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    color: ${({ theme }) => theme.colors.primaryDark};
    margin-left: 64px;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    ${H2Styles}
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const ViewAllText = styled.a`
  ${S2Styles}
  display:block;
  color: ${({ theme }) => theme.colors.neutral7};
  cursor: pointer;
  margin-right: 8px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    margin-right: 64px;
  }
`;

type ProductPageCarouselProps = {
  creatorCollections: CollectionEdgeResponse[];
  title?: string;
};

const FeaturedCreatorsCarousel: React.FC<ProductPageCarouselProps> = ({
  creatorCollections,
  title,
}: ProductPageCarouselProps) => {
  const { state } = useStore();
  const [isMaxViewport, setIsMaxViewport] = useState<boolean>(false);
  const size = useWindowSize();
  const theme = useContext(ThemeContext);
  const collectionsWithoutForsaken = useMemo(
    () => creatorCollections.filter((collection) => !collection.node?.id.includes('310093906076')),
    [creatorCollections],
  );

  const slicedCreatorCollections = useMemo(() => collectionsWithoutForsaken.slice(0, 7), [collectionsWithoutForsaken]);

  useEffect(() => {
    if (!size.width) {
      return;
    }
    // eslint-disable-next-line react/destructuring-assignment
    const isMaxViewport = size.width >= theme.breakpoints.maxContent;
    if (isMaxViewport) {
      setIsMaxViewport(true);
    }
  }, [size]);

  const featuredCreatorsTitle = 'Featured Creators';

  return (
    <ProductCarouselWrapper>
      <ProductCarouselHeader>
        <FeaturedCreatorsHeading>{title || featuredCreatorsTitle}</FeaturedCreatorsHeading>
        <Link href={routes.featuredCreators} passHref>
          <ViewAllText
            onClick={() => {
              posthog.capture(`homepage_carouselViewAllLink_click`, {
                link_href: routes.featuredCreators,
                link_title: featuredCreatorsTitle,
                is_logged_in: isUserLoggedIn(state.user),
              });
            }}
          >
            View All
          </ViewAllText>
        </Link>
      </ProductCarouselHeader>
      {isMaxViewport ? (
        <Container>
          <CreatorScroller creatorCollections={slicedCreatorCollections} sectionName="home_featured_creators" />
        </Container>
      ) : (
        <CreatorScroller creatorCollections={slicedCreatorCollections} sectionName="home_featured_creators" />
      )}
    </ProductCarouselWrapper>
  );
};

export default FeaturedCreatorsCarousel;
